import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import { useGetBillingQuery } from '@/api/billing'
import { useSelector } from 'react-redux'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import WidgetPaper from '@/components/WidgetPaper'
import TableRowsSkeleton from '@/components/TableRowsSkeleton'
import TableTopBar from './TableTopBar'

const Billing = ({
  ref
}) => {
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [apiPage, setApiPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const { data: billings, isLoading: isBillingLoading, isFetching } = useGetBillingQuery({
    buildingId: activeBuildingId,
    plate_number: search,
    page: apiPage
  })
  const showSkeleton = isBillingLoading || (isFetching && !billings?.data?.length)
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    if (newPage > page) {
      setApiPage((prev) => prev + 1)
    } else {
      setApiPage((prev) => prev - 1)
    }
  }

  const handleSearch = (value) => {
    setPage(0)
    setApiPage(1)
    setSearch(value)
  }

  return (
    <WidgetPaper
      ref={ref}
      title={t('billing')}
    >
      <Stack>
        <TableTopBar
          onSearchChange={handleSearch}
        />
        <TableContainer
          id="table-container"
          sx={{ margin: 'auto' }}
        >
          <Table aria-label="simple table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('export_field_id')}</TableCell>
                <TableCell>{t('plate_number')}</TableCell>
                <TableCell>{t('date_of_arrival')}</TableCell>
                <TableCell>{t('paid')}</TableCell>
                <TableCell>{t('date_of_payment')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                showSkeleton ? (
                  <TableRowsSkeleton rows={rowsPerPage} columns={5} />
                ) : (
                  billings?.data?.map((item) => (
                    <TableRow key={item.id} tabIndex={-1}>
                      <TableCell>{item?.id}</TableCell>
                      <TableCell>{item?.plate_number}</TableCell>
                      <TableCell>
                        {item?.entrance_time
                          ? format(
                              new Date(item?.entrance_time.replace('Z', '')),
                              'dd.MM.yyyy HH:mm'
                            )
                          : '-'}
                      </TableCell>
                      <TableCell>{item?.paid_amount}</TableCell>
                      <TableCell>
                        {item?.paid_time?.Time
                          ? format(
                              new Date(item?.paid_time.Time.replace('Z', '')),
                              'dd.MM.yyyy HH:mm'
                            )
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ 10, 25 ]}
          component="div"
          count={billings?.metadata?.total_count || 1}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('rows_per_page')}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t('of')} ${count !== -1 ? count : `${to}+`}`
          }
        />
      </Stack>
    </WidgetPaper>
  )
};

export default Billing